<script setup lang="ts">
import {
  DIRECTION_DELIVERY,
  UNASSIGNED,
  licensePlateRegex,
  COUNTER_CANCELLATION_NEEDED,
} from "@/utils/constants";
import { PropType, ref } from "vue";
import ArrowBackward from "@/components/icons/ArrowBackward.vue";
import ArrowForward from "@/components/icons/ArrowForward.vue";
import ElectricLocomotive from "@/components/icons/ElectricLocomotive.vue";
import OrderDetails from "./OrderDetails.vue";
import StatusChangeButton from "@/components/StatusChangeButton.vue";
import router from "@/router";
import OrderTrainInfo from "./OrderTrainInfo.vue";
import store from "@/store";
const props = defineProps({
  order: {
    type: Object,
    default: () => ({
      orderId: null,
      orderPosition: null,
      primaryReference: null,
      secondaryReference: null,
      directionType: null,
      transportationStatus: null,
      loading_unit_formatted: null,
      loadUnit: {
        identifierPrefix: null,
        identifierNumber: null,
        type: null,
        lengthCode: null,
        heightCode: null,
        emptyWeight: null,
        loadWeight: null,
        totalWeight: null,
        loadingStatus: null,
        dangerousGoods: false, // Set default value for boolean properties
        waste: false,
        limitedQuantity: false,
        customsGoods: false,
      },
      train: {
        trainNumber: null,
        operatorId: null,
        operatorName: null,
        shippingDate: null,
        trainClosingTime: null,
        trainArrivalTime: null,
        trainState: null,
      },
      terminal: {
        terminalId: {
          locationCode: null,
          authority: null,
        },
        terminalName: null,
      },
      freightForwarderId: null,
      freightForwarderName: null,
      licensePlate: null,
      driverId: null,
    }),
  },
  index: Number,
  activeOrder: {
    type: [Number, null] as PropType<number | null>,
    default: null,
  },
});

const emit = defineEmits(["activeOrder", "refreshData"]);
const showTrainInfo = ref(false);

const isNotProduction = () => {
  return process.env.NODE_ENV !== "production";
};

const toggleOrder = () => {
  if (props.activeOrder == null) {
    router.push(
      `/assignments/${props.order.orderId}/${props.order.orderPosition}/${props.order.directionType}`
    );
  } else {
    router.push("/");
  }
};

const formatKg = (value: string | number) => {
  if (!value) return;
  value = parseFloat(value as string);
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") + " kg";
};

const licensePlateInvalid = () => {
  if (!store.state.user.data.license_plate) return true;
  return !licensePlateRegex.test(store.state.user.data.license_plate);
};

const statusChangeButtonDisabled = () => {
  if (
    props.order.roadTransportAssignmentStatus === UNASSIGNED ||
    licensePlateInvalid()
  ) {
    return true;
  }
  return false;
};

const toggleTrainInfo = () => {
  if (!isNotProduction()) return;
  showTrainInfo.value = !showTrainInfo.value;
  emit("activeOrder", showTrainInfo.value ? props.index : null);
};
</script>
<template>
  <div
    v-if="activeOrder == null || activeOrder == index"
    class="orderCard bg-white w-full rounded-lg shadow-xl"
    :order-id="order.orderId"
  >
    <div
      @click="toggleOrder"
      :class="{
        'opacity-50':
          order.transportationStatus === COUNTER_CANCELLATION_NEEDED,
      }"
      class="grid grid-cols-3 mx-2 border-b pt-2"
    >
      <div class="col-span-2 flex">
        <div class="w-20 h-14 mt-2">
          <ArrowForward
            v-if="order?.directionType?.toLowerCase() == DIRECTION_DELIVERY"
          ></ArrowForward>
          <ArrowBackward v-else></ArrowBackward>
        </div>
        <div class="flex flex-col justify-start pt-2 text-left">
          <span
            v-if="order?.directionType?.toLowerCase() == DIRECTION_DELIVERY"
            class="font-bold text-md"
            >{{ $t("order.delivery") }}</span
          >
          <span v-else class="font-bold text-md">{{
            $t("order.pick_up")
          }}</span>
          <span class="font-normal text-sm">{{
            order.terminal.terminalName
          }}</span>
          <span v-if="isNotProduction()" class="font-normal text-sm"
            >Status: {{ order.transportationStatus }}</span
          >
        </div>
      </div>

      <div class="flex flex-col pt-2 text-left break-all">
        <span class="font-normal text-sm"
          >{{ $t("order.ref") }}: {{ order.primaryReference }}</span
        >
        <span class="font-normal text-sm"
          >{{ $t("order.origRef") }}: {{ order.secondaryReference }}</span
        >
        <span v-if="activeOrder == null" class="font-normal text-sm">
          {{ order?.loadUnit?.identifierPrefix
          }}{{ order?.loadUnit?.identifierNumber }}</span
        >
        <span
          v-if="activeOrder == null"
          class="font-normal d-flex flex-col text-sm"
          ><div class="font-normal d-flex gap-1 text-sm">
            {{ $t("order.loadWeightShort") }}:
            {{ formatKg(order?.loadUnit?.loadWeight) }}
          </div>
          <div class="font-normal d-flex gap-1 text-sm">
            {{ $t("order.emptyWeightShort") }}:
            {{ formatKg(order?.loadUnit?.emptyWeight) }}
          </div></span
        >
      </div>
      <div
        class="col-span-3"
        v-if="
          order.loadUnit.customsGoods &&
          (order.loadUnit.dangerousGoods ||
            order.loadUnit.waste ||
            order.loadUnit.limitedQuantity)
        "
      >
        <span class="text-red-600 flex items-center justify-center">
          <db-icon icon="info" variant="24-outline"></db-icon>
          <span v-if="order.loadUnit.dangerousGoods">{{
            $t("order.error_customs_with_dangerous", {
              type: $t("order.dangerous_goods"),
            })
          }}</span>
          <span v-if="order.loadUnit.waste">{{
            $t("order.error_customs_with_dangerous", {
              type: $t("order.waste"),
            })
          }}</span>
          <span v-if="order.loadUnit.limitedQuantity">{{
            $t("order.error_customs_with_dangerous", {
              type: $t("order.limitedQuantity"),
            })
          }}</span>
        </span>
      </div>
      <div class="col-span-3" v-else-if="order.loadUnit.customsGoods">
        <span class="text-red-600 flex items-center justify-center">
          <db-icon icon="info" variant="24-outline"></db-icon>
          <span>{{ $t("order.error_customs") }}</span>
        </span>
      </div>
      <div
        class="col-span-3"
        v-else-if="
          order.loadUnit.dangerousGoods ||
          order.loadUnit.waste ||
          order.loadUnit.limitedQuantity
        "
      >
        <span class="text-red-600 flex items-center justify-center">
          <db-icon icon="info" variant="24-outline"></db-icon>
          <span v-if="order.loadUnit.dangerousGoods">{{
            $t("order.error_goods", { type: $t("order.dangerous_goods") })
          }}</span>
          <span v-if="order.loadUnit.waste">{{
            $t("order.error_goods", { type: $t("order.waste") })
          }}</span>
          <span v-if="order.loadUnit.limitedQuantity">{{
            $t("order.error_goods", { type: $t("order.limitedQuantity") })
          }}</span>
        </span>
      </div>
    </div>
    <div v-if="order.transportationStatus === COUNTER_CANCELLATION_NEEDED">
      <div class="flex pt-4 pb-2 pl-3">
        <MyTravel color="red"></MyTravel>
        <p class="text-red-500 pr-2">
          {{ $t("tour.counter_cancellation_needed") }}
        </p>
      </div>
    </div>
    <div
      v-else-if="activeOrder == null || showTrainInfo"
      class="flex items-center justify-between p-2"
    >
      <div class="pt-3">
        <ElectricLocomotive @click="toggleTrainInfo"></ElectricLocomotive
        >{{ order.train.trainNumber }}
      </div>
      <StatusChangeButton
        v-if="!showTrainInfo"
        :order="order"
        :index="index"
        classList="hydrated mt-2 px-2"
        variant="primary"
        :disabled="statusChangeButtonDisabled()"
      ></StatusChangeButton>
    </div>
    <OrderDetails
      v-else-if="order"
      @close="toggleOrder()"
      @refreshData="$emit('refreshData')"
      :order="order"
      :index="index"
    ></OrderDetails>
    <OrderTrainInfo
      @close="toggleTrainInfo"
      :order="order"
      v-if="showTrainInfo"
    ></OrderTrainInfo>
  </div>
</template>
